import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PropTypes from 'prop-types';
import { timeInputCategory } from '../Forms/Fields/TimePicker/TimePickerInput/index';
import DateTimeInput from './DateTimeInput/index';
import { constructDateObject } from '../../utils/time/index';
import {
  TIME_HOUR,
  TIME_MINUTE,
} from '../Forms/Fields/TimePicker/TimePickerItems/index';
import styles from './index.module.scss';
import { defaultDateSeparator } from './DatePicker.js';
import { CalendarIcon, CloseIcon } from '../../Icons';
import { Box, makeStyles } from '@material-ui/core';
import IconWithText from 'components/IconWithText/iconWithText';

dayjs.extend(customParseFormat);

export const DATE_DAY = 'date';
export const DATE_MONTH = 'months';
export const DATE_YEAR = 'years';

const useStyles = makeStyles(theme => ({
  customInputColors: {
    border: `1px solid ${theme.palette.text.secondary}`,
    background: theme.palette.background.default,
    '& [class*="MuiInputBase-adornedEnd"]': {
      color: theme.palette.text.primary,
    },
    '& [class*="customInput"]': {
      color: theme.palette.text.primary,
    },
    '& [class*="icon"]': {
      background: theme.palette.primary.main,
      '& [id="Layer_1"]': {
        color: `${theme.palette.secondary.light} !important`,
        '& [class*="st0"]': {
          fill: `${theme.palette.secondary.light} !important`,
        },
      },
      '& [class*="iconClear"]': {
        color: theme.palette.secondary.light,
      },
    },
  },
}));

const dateInput = [
  {
    category: DATE_DAY,
    min: '1',
    max: 31,
    separator: defaultDateSeparator,
    placeholder: 'DD',
  },
  {
    category: DATE_MONTH,
    min: '1',
    max: 12,
    separator: defaultDateSeparator,
    placeholder: 'MM',
  },
  {
    category: DATE_YEAR,
    maxLength: 4,
    placeholder: 'YYYY',
  },
];

const getDateTimeInput = showDateTime =>
  showDateTime ? [...dateInput, ...timeInputCategory] : dateInput;

const CustomInput = ({
  value,
  onClick,
  handleCustomChange,
  dateFormat,
  timeInterval,
  showDateTime,
  compact,
  className,
  disabled,
  maxMinuteInput,
}) => {
  const classes = useStyles();
  const [time, setTime] = useState();

  useEffect(() => {
    if (value) {
      setTime(constructDateObject(value, dateFormat));
    } else {
      setTime({
        [DATE_DAY]: '',
        [DATE_MONTH]: '',
        [DATE_YEAR]: '',
        [TIME_HOUR]: '',
        [TIME_MINUTE]: '',
      });
    }
  }, [dateFormat, value]);

  const handleOnChange = event => {
    const category = event.target.getAttribute('category');
    const newTime = { ...time, [category]: event.target.value };
    setTime({ ...time, [category]: event.target.value });
    const dateOnly = `${newTime[DATE_MONTH]}-${newTime[DATE_DAY]}-${newTime[DATE_YEAR]}`;
    const dateString = showDateTime
      ? `${dateOnly} ${newTime[TIME_HOUR]}:${newTime[TIME_MINUTE]}`
      : dateOnly;

    if (dateString === '-- 00:00' || dateString === '--') {
      handleCustomChange('');
      return;
    }

    if (dateString.length !== dateFormat.length) {
      return;
    }

    if (!dayjs(dateString).isValid()) {
      return;
    }

    handleCustomChange(dateString);
  };

  const clearDateInput = () => {
    setTime({
      [DATE_DAY]: '',
      [DATE_MONTH]: '',
      [DATE_YEAR]: '',
      [TIME_HOUR]: '',
      [TIME_MINUTE]: '',
    });
    handleCustomChange('');
  };

  return (
    <Box
      display={'flex'}
      width={'100%'}
      className={classnames(
        classes.customInputColors,
        styles.customInputWrapper,
        classes.customInput,
        className,
        {
          [styles.compact]: compact,
        },
      )}
    >
      {getDateTimeInput(showDateTime).map(
        (
          {
            category,
            min = 0,
            max,
            maxLength = 2,
            step = 1,
            separator,
            placeholder,
          },
          index,
        ) => {
          return (
            <DateTimeInput
              className={classnames(styles.block, styles.customInput)}
              key={category}
              date={time ? time[category] : ''}
              index={index}
              handleChange={handleOnChange}
              inputProps={{
                min,
                max: maxMinuteInput || max,
                category,
                maxLength,
                placeholder,
                step: timeInterval || step,
              }}
              {...(separator && { endAdornment: separator })}
            />
          );
        },
      )}
      <div className={styles.icon}>
        <IconWithText
          onClick={onClick}
          title={'Calendar'}
          component={CalendarIcon}
        />
        {/* <IconButton
          aria-label=""
          size="small"
          className={classnames(styles.iconButton, styles.iconCalendar)}
          disabled={disabled}
        >
          <CalendarIcon />
        </IconButton> */}

        <IconButton
          aria-label="Delete"
          onClick={clearDateInput}
          size="small"
          className={classnames(styles.iconButton, styles.iconClear, {
            [styles.show]: Boolean(value),
          })}
          disabled={disabled}
        >
          <CloseIcon />
        </IconButton>
      </div>
    </Box>
  );
};

CustomInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  handleCustomChange: PropTypes.func.isRequired,
  dateFormat: PropTypes.string,
  className: PropTypes.string,
  timeInterval: PropTypes.number,
  maxMinuteInput: PropTypes.number,
  showDateTime: PropTypes.bool,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default CustomInput;
